/**
 * Workspace, membership and invitation state (CLAUDE.md 11, 12.1; ADR-0002).
 *
 * The team half of the product had tested endpoints and no client at all. These are
 * the hooks that close that gap, written to the same rule as `hooks.ts`: TanStack
 * Query owns everything the server returns, and nothing here decides a permission --
 * the caller's `capabilities` array says what to *render*, and the API authorises
 * independently on every request (11.7).
 *
 * Query keys are prefixed by the workspace id wherever the resource is scoped to one,
 * so switching workspaces cannot show one firm's member list under another's name.
 * `workspaceKeys.current` deliberately reuses the key `WorkspaceSettings` already
 * writes, so a rename saved there and the header both refresh from one invalidation.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import type {
  InvitationPeek,
  Letterhead,
  Workspace,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceRole,
  WorkspaceUsage,
} from '@/api/types'

export const workspaceKeys = {
  current: ['workspace', 'current'] as const,
  members: (workspaceId: number | null) => ['workspace', workspaceId, 'members'] as const,
  invitations: (workspaceId: number | null) => ['workspace', workspaceId, 'invitations'] as const,
  usage: (workspaceId: number | null) => ['workspace', workspaceId, 'usage'] as const,
  invitation: (token: string) => ['invitation', token] as const,
}

/** The active workspace, as the server resolved it from `X-Workspace-Id`. */
export function useWorkspace() {
  return useQuery({
    queryKey: workspaceKeys.current,
    queryFn: () => api.get<Workspace>('/workspaces/current'),
  })
}

export function useWorkspaceMembers(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceKeys.members(workspaceId),
    queryFn: () => api.get<WorkspaceMember[]>(`/workspaces/${workspaceId}/members`),
    enabled: workspaceId !== null && enabled,
  })
}

export function useInvitations(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceKeys.invitations(workspaceId),
    queryFn: () => api.get<WorkspaceInvitation[]>(`/workspaces/${workspaceId}/invitations`),
    enabled: workspaceId !== null && enabled,
  })
}

/**
 * Where the browser fetches a workspace's logo.
 *
 * A full path rather than an API-client call, because the bytes are read through
 * `useBlobUrl` -- an `<img src>` carries no bearer token, so the image is fetched and
 * handed to the element as a `blob:` URL instead (see `components/support/useBlobUrl`).
 */
export function workspaceLogoPath(workspaceId: number): string {
  return `/api/v1/workspaces/${workspaceId}/logo`
}

/**
 * Save workspace settings, including the letterhead text.
 *
 * Only what changed is sent: a PATCH echoing every field back can silently revert a
 * colleague's concurrent edit. Sending `letterhead: null` clears the letterhead
 * outright, which is how the panel's clear button works.
 */
export function useUpdateWorkspace(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { letterhead?: Letterhead | null } & Record<string, unknown>) =>
      api.patch<Workspace>(`/workspaces/${workspaceId}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.current })
    },
  })
}

/**
 * Upload or replace the letterhead logo.
 *
 * The server re-encodes what it stores, so the file that comes back out is never the
 * file that went in -- which is why the response is the whole workspace rather than
 * an echo of the upload, and why the preview is refetched rather than assumed.
 */
export function useUploadWorkspaceLogo(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return api.upload<Workspace>(`/workspaces/${workspaceId}/logo`, form)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.current })
    },
  })
}

export function useDeleteWorkspaceLogo(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<Workspace>(`/workspaces/${workspaceId}/logo`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.current })
    },
  })
}

export function useWorkspaceUsage(workspaceId: number | null, enabled = true) {
  return useQuery({
    queryKey: workspaceKeys.usage(workspaceId),
    queryFn: () => api.get<WorkspaceUsage>(`/workspaces/${workspaceId}/usage`),
    enabled: workspaceId !== null && enabled,
  })
}

/**
 * Create an organisation. The caller becomes its owner.
 *
 * No cache invalidation here: the workspace *list* lives in `AuthProvider`, not in
 * TanStack Query, because the router and the request header both depend on it. The
 * caller refreshes it through `refreshWorkspaces` and then switches.
 */
export function useCreateWorkspace() {
  return useMutation({
    mutationFn: (input: { name: string; locale_default?: string; default_code_id?: string }) =>
      api.post<Workspace>('/workspaces', input),
  })
}

/**
 * Delete an organisation.
 *
 * 409 `error.workspace.has_projects` while a live project remains, and 422 on a
 * personal workspace -- both surfaced to the user as the server's own message, since
 * "transfer or delete the 3 remaining projects" is the actionable half.
 */
export function useDeleteWorkspace() {
  return useMutation({
    mutationFn: (workspaceId: number) => api.delete<void>(`/workspaces/${workspaceId}`),
  })
}

export function useUpdateWorkspaceMember(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: WorkspaceRole }) =>
      api.patch<WorkspaceMember>(`/workspaces/${workspaceId}/members/${userId}`, { role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.members(workspaceId) })
      // The caller may have just demoted themselves out of managing this page.
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.current })
    },
  })
}

export function useRemoveWorkspaceMember(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) =>
      api.delete<void>(`/workspaces/${workspaceId}/members/${userId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.members(workspaceId) })
      // Their projects passed to the workspace owner, so ownership on screen is stale.
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

/**
 * Invite by email.
 *
 * The response carries the token **once** -- the server kept only a hash -- so the
 * caller must put it on screen immediately. There is no endpoint to ask for it again,
 * by design, and resending mints a different one.
 */
export function useInviteMember(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; role: WorkspaceRole }) =>
      api.post<WorkspaceInvitation>(`/workspaces/${workspaceId}/invitations`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.invitations(workspaceId) })
    },
  })
}

/** Reissue an invitation. The previous link stops working the moment this returns. */
export function useResendInvitation(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: number) =>
      api.post<WorkspaceInvitation>(
        `/workspaces/${workspaceId}/invitations/${invitationId}/resend`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.invitations(workspaceId) })
    },
  })
}

export function useRevokeInvitation(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: number) =>
      api.post<void>(`/workspaces/${workspaceId}/invitations/${invitationId}/revoke`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.invitations(workspaceId) })
    },
  })
}

/**
 * What an invitation link is for, before the recipient commits to anything.
 *
 * Unauthenticated by necessity: the recipient may have no account yet, and sending
 * them to a sign-in form before telling them what they are signing in *for* is how an
 * invitation gets abandoned. A dead token answers 404, which is an answer rather than
 * a failure, so it is not retried.
 */
export function useInvitationPeek(token: string | undefined) {
  return useQuery({
    queryKey: workspaceKeys.invitation(token ?? ''),
    queryFn: () =>
      api.request<InvitationPeek>(`/invitations/${token}`, { method: 'GET', auth: false }),
    enabled: Boolean(token),
    retry: false,
  })
}

export function useAcceptInvitation() {
  return useMutation({
    mutationFn: (token: string) => api.post<Workspace>(`/invitations/${token}/accept`),
  })
}
